import { useState, useEffect } from 'react';
import LoadingSpinner        from '../components/LoadingSpinner';
import ErrorCard             from '../components/ErrorCard';
import SandboxForecastPanel  from '../components/SandboxForecastPanel';
import ScenarioCard          from '../components/ScenarioCard';
import { useSandbox }        from '../hooks/useSandbox';

function fmt(n) {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n);
  const str = abs >= 1000 ? `$${(abs/1000).toFixed(1)}k` : `$${abs.toFixed(0)}`;
  return n < 0 ? `-${str}` : str;
}

function Sandbox() {
  const { grid, baseline, loading, error, updateCell,
          resetGrid, isModified, calcNetIncome, baselineNet, reload } = useSandbox();

  const [scenarioName,   setScenarioName]   = useState('New Scenario');
  const [saving,         setSaving]         = useState(false);
  const [saveMsg,        setSaveMsg]        = useState('');
  const [scenarios,      setScenarios]      = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [promoting,      setPromoting]      = useState(false);
  const [resetKey,       setResetKey]       = useState(0);
  
  // Load saved scenarios — reload after every save
  useEffect(() => {
    fetch('/api/scenarios')
      .then(r => r.json())
      .then(d => setScenarios(d.scenarios || []))
      .catch(() => {});
  }, [saveMsg]);

  if (loading) return <div className="page"><LoadingSpinner message="Loading sandbox data..." /></div>;
  if (error)   return <div className="page"><ErrorCard error={error} onRetry={reload} /></div>;
  if (!grid)   return null;

  const monthNames    = grid.monthNames || [];
  const visibleMonths = monthNames.map((m, i) => ({ name: m.substring(0,3), index: i }));
  const allRows       = grid.rows || [];
  const otherIncomeRows2  = allRows.filter(r =>
    r.section === 'Other Income' || r.section === 'OtherIncome'
  );
  const otherExpenseRows2 = allRows.filter(r =>
    r.section === 'Other Expense' || r.section === 'OtherExpense' ||
    r.section === 'Other Expenses' || r.section === 'OtherExpenses'
  );
  const monthCount    = visibleMonths.length;
  const closedCount   = grid.closedMonths;
  const net           = calcNetIncome();

  // ── P&L GROUPING — section-based (matches server response) ──
  const incomeRows = allRows.filter(r =>
    r.section === 'Income' &&
    r.section !== 'Other Income' &&
    r.section !== 'OtherIncome'
  );

  const cogsRows = allRows.filter(r =>
    r.section === 'COGS' || r.section === 'Cost of Goods Sold'
  );

  const unbudgetedRows = (allRows || []).filter(r => r.isUnbudgeted === true);

  const expenseRows = allRows.filter(r =>
    !r.isUnbudgeted &&
    r.section !== 'Other Expense' &&
    r.section !== 'OtherExpense' &&
    r.section !== 'Other Expenses' &&
    r.section !== 'OtherExpenses' &&
    r.section !== 'Other Income' &&
    r.section !== 'OtherIncome' &&
    (r.section === 'Expense' || r.section === 'Expenses')
  );

  const sumMonths = (rows) =>
    Array.from({ length: monthCount }, (_, i) =>
      rows.reduce((s, r) => s + (r.months?.[i]?.value || 0), 0));

  const incomeMonthly  = sumMonths(incomeRows);
  const cogsMonthly    = sumMonths(cogsRows);
  const expenseMonthly = sumMonths(expenseRows);
  const otherIncomeMonthly  = sumMonths(otherIncomeRows2);
  const otherExpenseMonthly = sumMonths(otherExpenseRows2);
  const unbudgetedMonthly   = sumMonths(unbudgetedRows);
  const netIncomeMonthly    = incomeMonthly.map((v, i) =>
    v - cogsMonthly[i] - expenseMonthly[i] +
    otherIncomeMonthly[i] - otherExpenseMonthly[i] - unbudgetedMonthly[i]
  );
  const grossMonthly   = incomeMonthly.map((v, i) => v - cogsMonthly[i]);
  const netMonthly     = incomeMonthly.map((v, i) => v - cogsMonthly[i] - expenseMonthly[i]);

  const totals = {
  inc:  incomeRows.reduce((s, r)  => s + r.total, 0),
  cogs: cogsRows.reduce((s, r)   => s + r.total, 0),
  exp:  expenseRows.reduce((s, r) => s + r.total, 0),
  incBudget:  incomeRows.reduce((s, r)  => s + r.budgetTotal, 0),
  cogsBudget: cogsRows.reduce((s, r)   => s + r.budgetTotal, 0),
  expBudget:  expenseRows.reduce((s, r) => s + r.budgetTotal, 0),
};
totals.gross         = totals.inc - totals.cogs;
totals.grossBudget   = totals.incBudget - totals.cogsBudget;
totals.net           = totals.inc - totals.cogs - totals.exp;
totals.netBudget     = totals.incBudget - totals.cogsBudget - totals.expBudget;
totals.otherInc      = otherIncomeRows2.reduce((s, r) => s + r.total, 0);
totals.otherExp      = otherExpenseRows2.reduce((s, r) => s + r.total, 0);
totals.unbudgeted      = unbudgetedRows.reduce((s, r) => s + r.total, 0);
totals.netIncome       = totals.net + totals.otherInc - totals.otherExp - totals.unbudgeted;
totals.otherIncBudget  = otherIncomeRows2.reduce((s, r) => s + r.budgetTotal, 0);
totals.otherExpBudget  = otherExpenseRows2.reduce((s, r) => s + r.budgetTotal, 0);
totals.netIncomeBudget = totals.netBudget + totals.otherIncBudget - totals.otherExpBudget;

  // ── COLLECT CHANGES ───────────────────────────────────
  const collectChanges = () => {
    const changes = [];
    if (!baseline || !grid) return changes;
    grid.rows.forEach((row, rowIdx) => {
      row.months.forEach((cell, mi) => {
        if (cell.isForecast) {
          const baseVal = baseline.rows[rowIdx]?.months[mi]?.value;
          if (baseVal !== cell.value) {
            changes.push({ category: row.category, monthIndex: mi,
                           originalValue: baseVal, newValue: cell.value });
          }
        }
      });
    });
    return changes;
  };

  const changeCount = collectChanges().length;
  const hasChanges  = changeCount > 0;

  // ── SAVE SCENARIO ─────────────────────────────────────
  const saveScenario = async () => {
    if (!scenarioName.trim()) { setSaveMsg('Enter a scenario name first.'); return; }
    const changes = collectChanges();
    if (changes.length === 0) { setSaveMsg('No changes to save — edit some forecast cells first.'); return; }
    setSaving(true); setSaveMsg('');
    try {
      const r = await fetch('/api/sandbox/save', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: scenarioName.trim(), changes }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setSaveMsg(`✓ Saved '${data.name}' with ${data.changeCount} changes.`);
      setScenarioName('New Scenario');
    } catch (e) { setSaveMsg(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  // ── LOAD SCENARIO ─────────────────────────────────────
  const loadScenario = async (scenario) => {
    try {
      await fetch(`/api/scenarios/${scenario.id}`);
      resetGrid();
      setResetKey(k => k + 1);
      setActiveScenario(scenario.id);
      setSaveMsg(`Loaded scenario: ${scenario.name}`);
    } catch (e) { setSaveMsg(`Error: ${e.message}`); }
  };

  // ── PROMOTE SCENARIO ──────────────────────────────────
  const promoteScenario = async (sid, sname) => {
    if (!window.confirm(`Promote '${sname}' as the active forecast?`)) return;
    setPromoting(true);
    try {
      const r = await fetch('/api/sandbox/promote', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenarioId: sid }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSaveMsg(`✓ '${sname}' promoted to active forecast.`);
    } catch (e) { setSaveMsg(`Error: ${e.message}`); }
    finally { setPromoting(false); }
  };

  // ── RENDER DATA ROW ───────────────────────────────────────
function renderRow(row) {
  const masterIdx = allRows.findIndex(r => r.category === row.category);
  const varColor  = row.variance >= 0 ? '#15803D' : '#BE123C';
  const rowBg     = row.isUnbudgeted ? '#FFFBEB' : undefined;

  return (
    <tr key={row.category} style={{ borderBottom:'1px solid #F1F5F9', background: rowBg }}>
      <td style={{ padding:'0.5rem 0.5rem 0.5rem 1.25rem',
                  fontSize:'0.85rem', color: row.isUnbudgeted ? '#92400E' : '#475569',
                  whiteSpace:'nowrap', maxWidth:180,
                  overflow:'hidden', textOverflow:'ellipsis' }}>
        <span title={row.category}>
          {row.displayName || row.leafName || row.category}
        </span>
      </td>
      {row.months.map((c, i) => {
        if (c.isActual) {
          return (
            <td key={i} style={{ textAlign:'right', background:'#F8FAFC',
                                 padding:'0 0.5rem', color:'#64748B', fontSize:'0.85rem' }}>
              {fmt(c.value)}
            </td>
          );
        }
        const modified = c.value !== (baseline?.rows[masterIdx]?.months[i]?.value);
        return (
          <td key={i} style={{ textAlign:'right', padding:'0 0.25rem',
                                background: modified ? '#FFFBEB' : '#fff',
                                border: modified ? '1px solid #F59E0B' : 'none' }}>
            <input
              key={`${resetKey}-${masterIdx}-${i}`}
              type="number"
              value={c.value}
              onFocus={e => e.target.select()}
              onChange={e => updateCell(masterIdx, i, e.target.value)}
              style={{
                width:'100%', border:'none', background:'transparent',
                textAlign:'right', fontSize:'0.85rem', padding:'0.2rem',
                outline:'none', color: modified ? '#B45309' : '#1E293B',
              }}
            />
          </td>
        );
      })}
      <td style={{ textAlign:'right', fontWeight:600, padding:'0 0.75rem',
                   position:'sticky', right:160, zIndex:1, background:'#fff' }}>
        {fmt(row.total)}
      </td>
      <td style={{ textAlign:'right', color:'#64748B', padding:'0 0.75rem',
                   position:'sticky', right:80, zIndex:1, background:'#fff' }}>
        {fmt(row.budgetTotal)}
      </td>
      <td style={{ textAlign:'right', fontWeight:600, color:varColor, padding:'0 0.75rem',
                   position:'sticky', right:0, zIndex:1, background:'#fff' }}>
        {row.variance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(row.variance))}
      </td>
    </tr>
  );
}

// FIND AND REPLACE the entire SubtotalRow function:
function SubtotalRow({ label, monthValues, total, budget=0, variance=0, bg='#F1F5F9', bold=false, color='#1B2A4A' }) {
  const vColor = variance >= 0 ? '#15803D' : '#BE123C';
  return (
    <tr style={{ background:bg, fontWeight: bold?700:600, borderTop:'2px solid #E2E8F0' }}>
      <td style={{ padding:'0.6rem 0.5rem', color,
                   fontSize: bold?'0.9rem':'0.85rem' }}>
        {label}
      </td>
      {monthValues.map((v, i) => (
        <td key={i} style={{ textAlign:'right', padding:'0 0.5rem',
                              color: v < 0 ? '#BE123C' : color }}>
          {fmt(v)}
        </td>
      ))}
      <td style={{ textAlign:'right', padding:'0 0.75rem',
                   fontWeight: bold?700:600, color,
                   position:'sticky', right:160, zIndex:1, background:bg }}>
        {fmt(total)}
      </td>
      <td style={{ textAlign:'right', padding:'0 0.75rem', color:'#64748B', fontWeight:400,
                   position:'sticky', right:80, zIndex:1, background:bg }}>
        {fmt(budget)}
      </td>
      <td style={{ textAlign:'right', padding:'0 0.75rem',
                   fontWeight: bold?700:600, color:vColor,
                   position:'sticky', right:0, zIndex:1, background:bg }}>
        {variance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(variance))}
      </td>
    </tr>
  );
}

  // ── SECTION HEADER ────────────────────────────────────
  function SectionHeader({ label }) {
    return (
      <tr style={{ background:'#1B2A4A' }}>
        <td colSpan={monthCount + 4}
          style={{ padding:'0.45rem 0.75rem', color:'#94A3B8',
                   fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.06em',
                   textTransform:'uppercase' }}>
          {label}
        </td>
      </tr>
    );
  }

  return (
    <div className="page">

      {/* ── HEADER ─────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between',
                    marginBottom:'1rem', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <h2 style={{ margin:0 }}>Sandbox:</h2>
          <input
            value={scenarioName}
            onChange={e => setScenarioName(e.target.value)}
            onFocus={e => e.target.select()}
            style={{ fontSize:'1.1rem', fontWeight:700, border:'none',
                     borderBottom:'2px solid #CBD5E1', background:'transparent',
                     color:'#0E7C7B', outline:'none', width:220 }}
          />
          {changeCount > 0 && (
            <span style={{ background:'#F59E0B', color:'#fff', borderRadius:12,
                           padding:'0.1rem 0.5rem', fontSize:'0.75rem', fontWeight:700 }}>
              {changeCount} change{changeCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <button
            onClick={() => { resetGrid(); setResetKey(k => k + 1); setScenarioName('New Scenario'); }}
            style={{ padding:'0.45rem 1rem', background:'#475569', color:'#fff',
                     border:'none', borderRadius:4, cursor:'pointer', fontSize:'0.85rem' }}>
            Reset
          </button>
          <button
            onClick={saveScenario}
            disabled={saving || !hasChanges}
            style={{ padding:'0.45rem 1rem', border:'none', borderRadius:4,
                     cursor: hasChanges ? 'pointer' : 'default', fontSize:'0.85rem',
                     fontWeight:600, color:'#fff',
                     background: hasChanges ? '#0E7C7B' : '#94A3B8' }}>
            {saving ? 'Saving...' : `Save "${scenarioName}"`}
          </button>
        </div>
      </div>

      {/* ── MESSAGES ───────────────────────────────────── */}
      {saveMsg && (
        <div style={{ padding:'0.4rem 0.75rem', borderRadius:4, marginBottom:'0.75rem',
                      fontSize:'0.85rem',
                      background: saveMsg.startsWith('✓') ? '#DCFCE7' : '#FFE4E6',
                      color:      saveMsg.startsWith('✓') ? '#15803D' : '#BE123C' }}>
          {saveMsg}
        </div>
      )}

      {/* ── NET IMPACT BANNER ──────────────────────────── */}
      {hasChanges && (
        <div style={{ background: net.variance >= 0 ? '#DCFCE7' : '#FFE4E6',
                      border:`1px solid ${net.variance >= 0 ? '#15803D' : '#BE123C'}`,
                      borderRadius:6, padding:'0.6rem 1rem', marginBottom:'1rem',
                      fontSize:'0.9rem', fontWeight:600,
                      color: net.variance >= 0 ? '#15803D' : '#BE123C' }}>
          {net.variance >= 0 ? '▲' : '▼'} Net impact: {fmt(Math.abs(net.variance))} {net.variance >= 0 ? 'improvement' : 'reduction'}
          &nbsp;·&nbsp; Forecast net: {fmt(net.forecastNet)} vs budget: {fmt(net.budgetNet)}
        </div>
      )}

      {/* ── LIVE FORECAST PANEL ────────────────────────── */}
      <SandboxForecastPanel net={net} baselineNet={baselineNet} />

      {/* ── GRID TABLE ─────────────────────────────────── */}
      <div style={{ overflowX:'auto', background:'#fff', borderRadius:8,
                    boxShadow:'0 1px 4px rgba(0,0,0,0.1)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>

          {/* Column headers */}
          <thead>
            <tr style={{ background:'#1B2A4A', color:'#fff' }}>
              <th style={{ padding:'0.8rem', textAlign:'left', position:'sticky', left:0,
                           background:'#1B2A4A', zIndex:2, minWidth:180 }}>Category</th>
              {visibleMonths.map(m => (
                <th key={m.index} style={{ textAlign:'right', padding:'0.6rem 0.4rem',
                                           minWidth:68, fontSize:'0.8rem',
                                           background: m.index < closedCount ? '#334155' : '#1B2A4A',
                                           color:      m.index < closedCount ? '#94A3B8'  : '#fff' }}>
                  {m.name}
                  {m.index < closedCount && <div style={{fontSize:'0.65rem',color:'#64748B'}}>ACT</div>}
                </th>
              ))}
              <th style={{ textAlign:'right', padding:'0.8rem', minWidth:80,
                          position:'sticky', right:160, zIndex:2,
                          background:'#1B2A4A' }}>Total</th>
              <th style={{ textAlign:'right', padding:'0.8rem', minWidth:80, color:'#94A3B8',
                          position:'sticky', right:80, zIndex:2,
                          background:'#1B2A4A' }}>Budget</th>
              <th style={{ textAlign:'right', padding:'0.8rem', minWidth:80, color:'#94A3B8',
                          position:'sticky', right:0, zIndex:2,
                          background:'#1B2A4A' }}>Var</th>
            </tr>
          </thead>

          <tbody>
            {/* INCOME */}
<SectionHeader label="📈 Income" />
{incomeRows.map(renderRow)}
<SubtotalRow label="Total Income" monthValues={incomeMonthly}
  total={totals.inc}
  budget={totals.incBudget}
  variance={totals.inc - totals.incBudget}
  bg="#EFF6FF" color="#0369A1" />

{/* COGS — only show if any rows */}
{cogsRows.length > 0 && (
  <>
    <SectionHeader label="🔧 Cost of Goods Sold" />
    {cogsRows.map(renderRow)}
    <SubtotalRow label="Total COGS" monthValues={cogsMonthly}
      total={totals.cogs}
      budget={totals.cogsBudget}
      variance={totals.cogsBudget - totals.cogs}
      bg="#FFF7ED" color="#92400E" />
    <SubtotalRow label="Gross Profit" monthValues={grossMonthly}
      total={totals.gross}
      budget={totals.grossBudget}
      variance={totals.gross - totals.grossBudget}
      bg="#F0FDF4" bold={true} color="#15803D" />
  </>
)}

{/* EXPENSES */}
<SectionHeader label="📉 Expenses" />
{expenseRows.map(renderRow)}
<SubtotalRow label="Total Expenses" monthValues={expenseMonthly}
  total={totals.exp}
  budget={totals.expBudget}
  variance={totals.expBudget - totals.exp}
  bg="#FFF1F2" color="#BE123C" />

{/* OTHER INCOME */}
{otherIncomeRows2.length > 0 && (
  <>
    <tr style={{ background:'#0369A1' }}>
      <td colSpan={monthCount + 4}
        style={{ padding:'0.45rem 0.75rem', color:'#DBEAFE',
                 fontSize:'0.75rem', fontWeight:700,
                 letterSpacing:'0.06em', textTransform:'uppercase' }}>
        📊 Other Income
      </td>
    </tr>
    {otherIncomeRows2.map(renderRow)}
  </>
)}

{/* OTHER EXPENSES */}
{otherExpenseRows2.length > 0 && (
  <>
    <tr style={{ background:'#7E22CE' }}>
      <td colSpan={monthCount + 4}
        style={{ padding:'0.45rem 0.75rem', color:'#EDE9FE',
                 fontSize:'0.75rem', fontWeight:700,
                 letterSpacing:'0.06em', textTransform:'uppercase' }}>
        📊 Other Expenses
      </td>
    </tr>
    {otherExpenseRows2.map(renderRow)}
  </>
)}

{/* UNBUDGETED ACTUALS */}
{unbudgetedRows.length > 0 && (
  <>
    <tr style={{ background: '#B45309' }}>
      <td colSpan={monthCount + 4}
        style={{ padding:'0.45rem 0.75rem', color:'#FEF3C7',
                 fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.06em',
                 textTransform:'uppercase' }}>
        ⚠ Unbudgeted Actuals — transactions with no budget row in QB
      </td>
    </tr>
    {unbudgetedRows.map(row => (
      <tr key={`unbudgeted-${row.category}`}
        style={{ borderBottom:'1px solid #FEF3C7', background:'#FFFBEB' }}>
        <td style={{
          padding:'0.5rem 0.5rem 0.5rem 1.25rem',
          fontSize:'0.85rem', color:'#B45309',
          whiteSpace:'nowrap', maxWidth:180,
          overflow:'hidden', textOverflow:'ellipsis',
        }}>
          <span title={row.category}>⚠ {row.displayName || row.category}</span>
        </td>
        {row.months.map((cell, mi) => (
          <td key={mi} style={{
            textAlign:'right', padding:'0 0.5rem',
            background: cell.isActual ? '#FEF3C7' : '#FFFBEB',
            color: cell.value !== 0 ? '#B45309' : '#CBD5E1',
            fontSize:'0.85rem',
          }}>
            {cell.value !== 0 ? `$${Math.abs(cell.value).toFixed(0)}` : '—'}
          </td>
        ))}
        <td style={{ textAlign:'right', fontWeight:600, padding:'0 0.75rem',
                     color:'#B45309', position:'sticky', right:160, zIndex:1,
                     background:'#FFFBEB' }}>
          ${row.total.toFixed(0)}
        </td>
        <td style={{ textAlign:'right', color:'#94A3B8', padding:'0 0.75rem',
                     position:'sticky', right:80, zIndex:1, background:'#FFFBEB' }}>—</td>
        <td style={{ textAlign:'right', fontWeight:600, color:'#B45309',
                     padding:'0 0.75rem', position:'sticky', right:0, zIndex:1,
                     background:'#FFFBEB' }}>
          ▼ ${row.total.toFixed(0)} unbudgeted
        </td>
      </tr>
    ))}
  </>
)}
          </tbody>

          <tfoot>
            {/* NET OPERATING INCOME */}
            <tr style={{ background:'#1B2A4A', color:'#fff', fontWeight:700 }}>
              <td style={{ padding:'0.7rem 0.75rem', position:'sticky', left:0,
                           background:'#1B2A4A', fontSize:'0.85rem' }}>
                NET OPERATING INCOME
              </td>
              {netMonthly.map((v, i) => (
                <td key={i} style={{ textAlign:'right', padding:'0 0.5rem',
                                     color: v >= 0 ? '#86EFAC' : '#FCA5A5',
                                     fontSize:'0.85rem' }}>
                  {fmt(v)}
                </td>
              ))}
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:700,
                           position:'sticky', right:160, zIndex:1, background:'#1B2A4A',
                           color: totals.net >= 0 ? '#86EFAC' : '#FCA5A5' }}>
                {fmt(totals.net)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', color:'#94A3B8',
                           position:'sticky', right:80, zIndex:1, background:'#1B2A4A' }}>
                {fmt(totals.netBudget)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:700,
                           position:'sticky', right:0, zIndex:1, background:'#1B2A4A',
                           color: (totals.net - totals.netBudget) >= 0 ? '#86EFAC' : '#FCA5A5' }}>
                {(totals.net - totals.netBudget) >= 0 ? '▲ ' : '▼ '}
                {fmt(Math.abs(totals.net - totals.netBudget))}
              </td>
            </tr>
            {/* NET INCOME — includes Other Income and Other Expenses */}
            <tr style={{ background:'#0F172A', color:'#fff', fontWeight:800,
                         borderTop:'2px solid #5EEAD4' }}>
              <td style={{ padding:'0.8rem 0.75rem', position:'sticky', left:0,
                           background:'#0F172A', fontSize:'0.9rem' }}>
                NET INCOME
              </td>
              {netIncomeMonthly.map((v, i) => (
                <td key={i} style={{ textAlign:'right', padding:'0 0.5rem',
                                     color: v >= 0 ? '#86EFAC' : '#FCA5A5',
                                     fontSize:'0.85rem' }}>
                  {fmt(v)}
                </td>
              ))}
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:800,
                           position:'sticky', right:160, zIndex:1, background:'#0F172A',
                           color: totals.netIncome >= 0 ? '#86EFAC' : '#FCA5A5',
                           fontSize:'0.9rem' }}>
                {fmt(totals.netIncome)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', color:'#94A3B8',
                           position:'sticky', right:80, zIndex:1, background:'#0F172A' }}>
                {fmt(totals.netIncomeBudget)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:800,
                           position:'sticky', right:0, zIndex:1, background:'#0F172A',
                           color: (totals.netIncome - totals.netIncomeBudget) >= 0 ? '#86EFAC' : '#FCA5A5',
                           fontSize:'0.9rem' }}>
                {(totals.netIncome - totals.netIncomeBudget) >= 0 ? '▲ ' : '▼ '}
                {fmt(Math.abs(totals.netIncome - totals.netIncomeBudget))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── SCENARIO PANEL ─────────────────────────────── */}
      <div style={{ marginTop:'2rem' }}>
        {scenarios.length > 0 ? (
          <>
            <h3 style={{ color:'#1B2A4A', marginBottom:'0.75rem', fontSize:'1rem' }}>
              Saved Scenarios
            </h3>
            <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap' }}>
              {scenarios.map(s => (
                <ScenarioCard key={s.id} scenario={s}
                  onLoad={loadScenario}
                  onPromote={() => promoteScenario(s.id, s.name)}
                  isActive={activeScenario === s.id} />
              ))}
            </div>
          </>
        ) : (
          <p style={{ color:'#94A3B8', fontSize:'0.85rem' }}>
            No saved scenarios yet. Edit forecast cells and click Save.
          </p>
        )}
      </div>

    </div>
  );
}

export default Sandbox;